import {
  MobileRow,
  MobileRowTitle,
  MobileRowValue,
  MobileTableRowWrap,
  MobileTableWrapper,
  NoResults
} from '@/components/table/mobileTable/mobileTable';

import { InvestorDetailsCommitmentsTabTableDataQuery, InvestorDetailsHouseholdCommitmentsTabTableDataQuery } from '@/__generated__/graphql';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { InvestorDetailsEntityTypeEnum } from '@/components/investors-page/investorDetailsPage/types';
import { TableBodyWrapper, TableRowCell, TextCell } from '@/components/table/styles';
import { TableColumns } from '@/components/table/types';
import { currencyFormatter, formatCash } from '@/shared/utils/currency';
import { getUTCDate } from '@/shared/utils/date';
import { themeVar } from '@/styles/theme';
import styled from 'styled-components';
import CommitmentsMobileSumFooter from './mobileSumFooter';

interface CommitmentsMobileTableProps {
  tableColumns: TableColumns[];
  tableRows: InvestorDetailsCommitmentsTabTableDataQuery['ClientEntityDetailsCommitments']['commitments'];
  entityType: InvestorDetailsEntityTypeEnum;
  householdCommitmentsTabTableTotals: InvestorDetailsHouseholdCommitmentsTabTableDataQuery['investorHouseholdCommitments']['totals'] | null;
  openOpportunityDetailsPage: (id: string) => void;
}

const CommitmentsMobileTable = ({
  tableColumns,
  tableRows,
  entityType,
  householdCommitmentsTabTableTotals,
  openOpportunityDetailsPage
}: CommitmentsMobileTableProps) => {
  const toolTipContent = (row: InvestorDetailsCommitmentsTabTableDataQuery['ClientEntityDetailsCommitments']['commitments'][number]) => {
    return (
      <TableBodyWrapper>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.committedCapital)} Committed Capital</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.capitalCalled)} Capital Called</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>{formatCash(row.capitalRemaining)} Capital Remaining</TextCell>
        </TableRowCell>
        <TableRowCell width={100}>
          <TextCell>{((row.capitalCalled / row.committedCapital) * 100).toFixed(2)}% Called to Date</TextCell>
        </TableRowCell>
      </TableBodyWrapper>
    );
  };

  const marketValueToolTipContent = (row: InvestorDetailsCommitmentsTabTableDataQuery['ClientEntityDetailsCommitments']['commitments'][number]) => {
    return (
      <TableBodyWrapper>
        {row.valuationDate && (
          <>
            <TableRowCell width={100}>
              <TextCell>Valuation Date: {row.valuationAmount ? getUTCDate(row.valuationDate) : 'None'}</TextCell>
            </TableRowCell>
            {row.valuationAmount !== null && (
              <TableRowCell width={100}>
                <TextCell>Valuation Amount: {currencyFormatter.format(row.valuationAmount)}</TextCell>
              </TableRowCell>
            )}
          </>
        )}
        {row.aggregateCallsAndDistributions !== null && (
          <TableRowCell width={100}>
            <TextCell>
              Aggregate Calls and Distributions since last valuation: {currencyFormatter.format(row.aggregateCallsAndDistributions)}
            </TextCell>
          </TableRowCell>
        )}
        <TableRowCell width={100}>
          <TextCell>Market Value: {currencyFormatter.format(row.marketValue)}</TextCell>
        </TableRowCell>
      </TableBodyWrapper>
    );
  };

  const getColorStatus = (status: string) => {
    if (status === 'Active') {
      return themeVar('context.success');
    } else if (status === 'Recommended' || status === 'Outstanding') {
      return themeVar('context.caution');
    } else {
      return '#111827';
    }
  };

  const TableRow = ({ el }: { el: InvestorDetailsCommitmentsTabTableDataQuery['ClientEntityDetailsCommitments']['commitments'][number] }) => {
    return (
      <MobileTableRowWrap key={el.id}>
        <MobileRow>
          <MobileRowTitle>{tableColumns[0].title}</MobileRowTitle>
          <MobileRowValueWrapper>
            <StrategyName isRedirect onClick={() => openOpportunityDetailsPage(el.investmentId)}>
              {el.name}
            </StrategyName>
            {(entityType === InvestorDetailsEntityTypeEnum.Household || entityType === InvestorDetailsEntityTypeEnum.Group) && (
              <EntityName>{el.entityName}</EntityName>
            )}
          </MobileRowValueWrapper>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[1].title}</MobileRowTitle>
          <MobileRowValue>{el.subAssetClass ? `${el.assetClass?.name} - ${el.subAssetClass.name}` : el.assetClass?.name}</MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[2].title}</MobileRowTitle>
          <MobileRowValue>
            <FormattingTooltip zIndex={1000}>{el.committedCapital}</FormattingTooltip>
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[3].title}</MobileRowTitle>
          <MobileRowValue>
            <CellWithTooltip>
              {((el.capitalCalled / el.committedCapital) * 100).toFixed(2)}%
              <Tooltip title={toolTipContent(el)}>
                <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
              </Tooltip>
            </CellWithTooltip>
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[4].title}</MobileRowTitle>
          <MobileRowValue>
            <FormattingTooltip zIndex={1000}>{+el.distributionsToDate}</FormattingTooltip>
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[5].title}</MobileRowTitle>
          <MobileRowValue>
            {el.valuationDate || el.valuationAmount || el.aggregateCallsAndDistributions || el.marketValue ? (
              <CellWithTooltip>
                {formatCash(el.marketValue)}
                <Tooltip title={marketValueToolTipContent(el)}>
                  <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                </Tooltip>
              </CellWithTooltip>
            ) : (
              <FormattingTooltip zIndex={1000}>{el.marketValue}</FormattingTooltip>
            )}
          </MobileRowValue>
        </MobileRow>

        <MobileRow>
          <MobileRowTitle>{tableColumns[6].title}</MobileRowTitle>
          <MobileRowValue>
            <ColorTitle color={getColorStatus(el.status)}>{el.status === 'Outstanding' ? 'Pending Commitment' : el.status}</ColorTitle>
          </MobileRowValue>
        </MobileRow>
      </MobileTableRowWrap>
    );
  };

  return (
    <MobileTableWrapper>
      {tableRows?.length ? (
        <>
          {tableRows.map((el, index) => {
            return <TableRow el={el} key={index} />;
          })}
          <CommitmentsMobileSumFooter
            tableColumns={tableColumns}
            tableRows={tableRows}
            entityType={entityType}
            householdCommitmentsTabTableTotals={householdCommitmentsTabTableTotals}
          />
        </>
      ) : (
        <NoResults>No Results</NoResults>
      )}
    </MobileTableWrapper>
  );
};

export default CommitmentsMobileTable;

const MobileRowValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 50%;
`;

const StrategyName = styled(MobileRowValue)`
  width: 100%;
`;

const EntityName = styled.p`
  color: ${themeVar('font.strong')};
`;

const ColorTitle = styled.div<{ color: string }>`
  font-family: 'Blinker', serif;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0;
  text-align: left;
  color: ${({ color }) => color};
  font-weight: 600;
`;

const CellWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
